// ============================================================
// components/auth/Login.tsx
//
// Web sign-in (/app/login), in the home page's design: email
// or phone (an SMS code), then "The full experience" panel.
// Accounts are created in the iPhone app, so a phone number
// without an account gets no code. Once signed in (or if
// already signed in) it goes to `?next=` when that is a path on
// this site, otherwise to the STASH Board.
// ============================================================
import React, { useEffect, useId, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Mail, Smartphone } from 'lucide-react';
import { loginAsync } from '../../actions/auth-actions';
import { safeNextPath } from '../../lib/login-redirect';
import { PhoneSignIn, phoneErrorMessage, toE164 } from '../../services/phone-sign-in';
import type { AppDispatch, RootState } from '../../store';
import { Reveal } from '../common/Reveal';
import { AppStoreBadge, BestOnIPhone } from '../landing/BestOnIPhone';
import { AndroidReferralProvider } from '../landing/AndroidWaitList';
import { ANDROID_REFERRAL } from '../../lib/android';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const rise =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 motion-safe:duration-700 motion-safe:fill-mode-both';

const labelClass = 'text-sm font-semibold text-foreground';
const fieldClass = 'h-11 text-base';
const submitClass = 'h-11 w-full text-base font-semibold';

function ErrorMessage({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p role="alert" className="m-0 rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
      {children}
    </p>
  );
}

function EmailSignIn(): React.JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const loginError = useSelector((state: RootState) => state.auth.loginError);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const emailId = useId();
  const passwordId = useId();

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void dispatch(loginAsync({ username: email.trim(), password }));
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={emailId} className={labelClass}>Email</label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldClass}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={passwordId} className={labelClass}>Password</label>
        <Input
          id={passwordId}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={fieldClass}
        />
      </div>
      <Button type="submit" disabled={Boolean(isLoading)} className={submitClass}>
        {isLoading ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}

type PhoneStep = 'number' | 'code' | 'no-account';

function PhoneSignInForm(): React.JSX.Element {
  const [phone] = useState(() => new PhoneSignIn());
  const [step, setStep] = useState<PhoneStep>('number');
  const [number, setNumber] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const numberId = useId();
  const codeId = useId();
  // reCAPTCHA needs a plain element id (useId's colons are fine in ids).
  const recaptchaId = `recaptcha-${useId().replace(/:/g, '')}`;

  useEffect(() => () => phone.dispose(), [phone]);

  const startOver = (): void => {
    setStep('number');
    setCode('');
    setError(null);
  };

  const sendCode = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    const e164 = toE164(number);
    if (!e164) {
      setError('Enter a mobile number, for example (615) 555-0123.');
      return;
    }
    setBusy(true);
    try {
      if (!(await phone.hasAccount(e164))) {
        setStep('no-account');
        return;
      }
    } catch {
      setError('Could not reach Stash. Check your connection and try again.');
      return;
    } finally {
      setBusy(false);
    }
    setBusy(true);
    try {
      await phone.sendCode(e164, recaptchaId);
      setStep('code');
    } catch (err) {
      setError(phoneErrorMessage((err as { code?: string }).code));
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Success signs in through Firebase; the auth listener updates the app
      // and the page then moves on to `next`.
      await phone.confirm(code.trim());
    } catch (err) {
      setError(phoneErrorMessage((err as { code?: string }).code));
    } finally {
      setBusy(false);
    }
  };

  const differentNumber = (
    <Button type="button" variant="ghost" onClick={startOver} className="self-center">
      Use a different number
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {step === 'number' && (
        <form onSubmit={(e) => void sendCode(e)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={numberId} className={labelClass}>Mobile number</label>
            <Input
              id={numberId}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(615) 555-0123"
              required
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className={fieldClass}
            />
            <p className="m-0 text-xs text-muted-foreground">We will text you a code. Message and data rates may apply.</p>
          </div>
          <Button type="submit" disabled={busy} className={submitClass}>
            {busy ? 'Sending…' : 'Send code'}
          </Button>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={(e) => void confirmCode(e)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={codeId} className={labelClass}>Code from the text</label>
            <Input
              id={codeId}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${fieldClass} tracking-[0.3em]`}
            />
            <p className="m-0 text-xs text-muted-foreground">Sent to {number.trim()}.</p>
          </div>
          <Button type="submit" disabled={busy} className={submitClass}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
          {differentNumber}
        </form>
      )}

      {step === 'no-account' && (
        <div className="flex flex-col gap-4">
          <div role="status" className="flex flex-col items-start gap-3 rounded-2xl border border-border/60 bg-muted/40 p-4">
            <p className="m-0 text-sm font-semibold text-foreground">No STASH account uses that number.</p>
            <p className="m-0 text-sm text-muted-foreground">
              Accounts are created in the iPhone app. Get the app to start your stash, then sign in here.
            </p>
            <AppStoreBadge className="h-10" />
          </div>
          {differentNumber}
        </div>
      )}

      {/* The invisible reCAPTCHA renders here. */}
      <div id={recaptchaId} />
    </div>
  );
}

function Login(): React.JSX.Element {
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));

  if (currentUser) return <Navigate to={next} replace />;

  return (
    // Android wait-list sign-ups from this page are attributed to the sign-in page.
    <AndroidReferralProvider value={ANDROID_REFERRAL.login}>
      <div className="flex flex-col gap-16 pb-16 font-sans md:gap-24">
        <section aria-labelledby="login-title" className="relative isolate overflow-hidden border-b border-border/40">
          {/* Atmosphere: soft brand glows, as on the home page */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[var(--stash-blue)]/25 blur-3xl" />
            <div className="absolute -right-16 top-10 h-80 w-80 rounded-full bg-[var(--stash-pink)]/30 blur-3xl" />
            <div className="absolute -bottom-40 left-1/3 h-96 w-96 rounded-full bg-[var(--stash-teal)]/20 blur-3xl" />
          </div>

          <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-4 py-14 md:grid-cols-[1.1fr_0.9fr] md:px-6 md:py-20">
            <div className={`flex flex-col items-start ${rise}`}>
              <h1
                id="login-title"
                className="m-0 text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl lg:text-6xl"
              >
                Sign in to{' '}
                <span className="bg-gradient-to-r from-[var(--stash-blue)] via-[color-mix(in_oklch,var(--stash-pink),var(--foreground)_35%)] to-[color-mix(in_oklch,var(--stash-teal),var(--foreground)_25%)] bg-clip-text text-transparent">
                  STASH
                </span>
              </h1>
              <p className="mt-5 mb-0 max-w-lg text-lg leading-relaxed text-muted-foreground">
                Browse your stash and your circle’s recommendations on the web. Use the email or mobile number from your
                STASH account.
              </p>
            </div>

            <div
              className={`w-full rounded-[2rem] border border-border/60 bg-card/85 p-6 shadow-xl backdrop-blur-md md:p-8 ${rise} motion-safe:delay-150`}
            >
              <Tabs defaultValue="email" className="flex flex-col gap-6">
                <TabsList className="grid h-11 w-full grid-cols-2 rounded-full bg-muted p-1">
                  <TabsTrigger value="email" className="rounded-full text-sm font-semibold">
                    <Mail aria-hidden="true" />
                    Email
                  </TabsTrigger>
                  <TabsTrigger value="phone" className="rounded-full text-sm font-semibold">
                    <Smartphone aria-hidden="true" />
                    Phone
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="email">
                  <EmailSignIn />
                </TabsContent>
                <TabsContent value="phone">
                  <PhoneSignInForm />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <Reveal>
            <BestOnIPhone />
          </Reveal>
        </div>
      </div>
    </AndroidReferralProvider>
  );
}

export default Login;
